import React from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, TextField, Chip, FormControlLabel, Checkbox, Link } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import api from "../Auth/Api";
import { navigate } from "../Layout/Router";
import { PageContainer, SectionHeader, DataTable, StatCard, FormDialog, relTime } from "../Layout/Common";

const REFRESH_MS = 60000;
const RANGES = [{ value: 24, label: "24h" }, { value: 72, label: "3d" }, { value: 168, label: "7d" }];

const pct = (v, digits = 2) => v == null ? "—" : `${v.toFixed(digits)}%`;
const num = (v, digits = 0) => v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });
const ms = (v) => v == null ? "—" : `${Math.round(v)} ms`;
const ratio = (used, limit) => limit > 0 ? Math.min(100, used / limit * 100) : 0;

// Azure availability-test run locations -> readable names.
const LOCATIONS = {
    "us-va-ash-azr": "Virginia (US)", "us-ca-sjc-azr": "California (US)", "us-tx-sn1-azr": "Texas (US)", "us-il-ch1-azr": "Illinois (US)",
    "us-fl-mia-edge": "Florida (US)", "latam-br-gru-edge": "São Paulo (BR)", "emea-nl-ams-azr": "Amsterdam (NL)", "emea-gb-db3-azr": "Dublin (IE)",
    "emea-fr-pra-edge": "Paris (FR)", "emea-ch-zrh-edge": "Zurich (CH)", "emea-se-sto-edge": "Stockholm (SE)", "emea-ru-msa-edge": "Moscow (RU)",
    "emea-au-syd-edge": "Sydney (AU)", "apac-sg-sin-azr": "Singapore (SG)", "apac-jp-kaw-edge": "Kawaguchi (JP)", "apac-hk-hkn-azr": "Hong Kong (HK)",
    "emea-w-uk-cst-azr": "Cardiff (UK)",
};
const locationName = (l) => LOCATIONS[l] ?? l;

const usageColor = (p) => p >= 90 ? "error.main" : p >= 70 ? "warning.main" : "success.main";

export default class SystemHealthPage extends React.Component {
    state = { data: null, hours: 24, loadedAt: null, settingsOpen: false, settings: null, saving: false };

    componentDidMount() {
        this.load();
        this.timer = setInterval(() => this.load(), REFRESH_MS);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    load = () => {
        const { hours } = this.state;
        return api.health("Loading system health", ["summary"], { payload: { hours }, silent: true })
            .then(r => { if (r && r.requests) this.setState({ data: r, loadedAt: Date.now() }); })
            .catch(() => {});
    }

    setRange = (e, hours) => {
        if (hours == null) return;
        this.setState({ hours }, this.load);
    }

    openSettings = () => this.setState({ settingsOpen: true, settings: { ...this.state.data.settings } });

    saveSettings = () => {
        const s = this.state.settings;
        const payload = {
            ...s,
            atlasConnections: Number(s.atlasConnections), atlasStorageGB: Number(s.atlasStorageGB), atlasRamGB: Number(s.atlasRamGB),
            maxInstances: Number(s.maxInstances), instanceMemoryMB: Number(s.instanceMemoryMB), alwaysReady: Number(s.alwaysReady),
            alertsEnabled: !!s.alertsEnabled, warnPct: Number(s.warnPct), clearPct: Number(s.clearPct), reminderHours: Number(s.reminderHours), replLagWarnSec: Number(s.replLagWarnSec),
            confirmSamples: Number(s.confirmSamples),
        };
        this.setState({ saving: true });
        api.health("Saving limits", ["settings-set"], { payload })
            .then(() => this.setState({ settingsOpen: false, saving: false }, this.load))
            .catch(() => this.setState({ saving: false }));
    }

    timeLabel = (t) => {
        const d = new Date(t);
        const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        return this.state.hours > 24 ? `${d.getMonth() + 1}/${d.getDate()} ${hm}` : hm;
    }

    renderCards() {
        const { data } = this.state;
        const { requests, atlas, azure, uptime, settings } = data;
        const total = requests.ok + requests.err + requests.rejected;
        const errRate = total > 0 ? requests.err / total * 100 : 0;
        const a = atlas.now, z = azure.now;
        const connPct = a ? ratio(a.connections, settings.atlasConnections) : 0;
        const storagePct = a ? ratio(a.storageMB, settings.atlasStorageGB * 1024) : 0;
        const instPct = z ? ratio(z.instances, settings.maxInstances) : 0;
        return (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <StatCard label="Uptime (probes)" value={pct(uptime.h24)}
                          secondary={uptime.d7 != null ? `7 days: ${pct(uptime.d7)}` : "no probe data yet"}
                          tertiary={azure.availability.locations.length ? `${azure.availability.locations.length} locations` : "availability test pending"} />
                <StatCard label="Requests OK" value={num(requests.ok)}
                          secondary={`${num(requests.err)} failed · ${num(requests.rejected)} rejected`}
                          tertiary={<>error rate {errRate.toFixed(2)}% · <Link component="button" underline="hover" onClick={() => navigate({ title: null, page: "health", sub: "errors", deep: [] })}>view errors</Link></>}
                          tertiaryColor={errRate >= 1 ? "error.main" : undefined} />
                <StatCard label="Latency p95" value={total > 0 ? ms(requests.p95) : "—"}
                          secondary={total > 0 ? `p50 ${ms(requests.p50)} · p99 ${ms(requests.p99)}` : "no player requests yet"}
                          tertiary={total > 0 ? <>avg {ms(requests.avgMs)} · <Link component="button" underline="hover" onClick={() => navigate({ title: null, page: "health", sub: "latency", deep: [] })}>per action</Link></> : undefined} />
                <StatCard label="Atlas connections" value={a ? `${num(a.connections)} / ${num(settings.atlasConnections)}` : "—"}
                          secondary={a ? `${connPct.toFixed(1)}% of ${settings.atlasTier}` : "no sample yet"}
                          tertiary={a ? `${num(a.opsPerSec, 1)} ops/s · lag ${a.replLagSec.toFixed(1)} s` : undefined} tertiaryColor={usageColor(connPct)} />
                <StatCard label="Atlas storage" value={a ? `${num(a.storageMB, 1)} MB` : "—"}
                          secondary={a ? `${storagePct.toFixed(2)}% of ${settings.atlasStorageGB} GB` : ""}
                          tertiary={a ? `data ${num(a.dataMB, 1)} · index ${num(a.indexMB, 1)} · RAM ${num(a.memResidentMB)} MB` : undefined} tertiaryColor={usageColor(storagePct)} />
                <StatCard label="Instances serving" value={z ? `${num(z.instances)} / ${num(settings.maxInstances)}` : "—"}
                          secondary={z ? `${instPct.toFixed(0)}% of scale-out cap (last 5-min window)` : "no sample yet"}
                          tertiary={z ? `always-ready ${num(z.alwaysReadyInstances, 1)} · cpu ${(z.cpuPct ?? 0).toFixed(0)}% avg · ${(z.cpuMaxPct ?? 0).toFixed(0)}% max` : undefined} tertiaryColor={usageColor(Math.max(instPct, z ? z.cpuPct ?? 0 : 0))} />
            </Box>
        );
    }

    chart(title, series, xLabels, yAxes, children, height = 260) {
        return (
            <Paper sx={{ p: 2, mb: 2, flex: 1, minWidth: 420 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
                {xLabels.length === 0
                    ? <Typography variant="body2" color="text.secondary">No samples in this range yet.</Typography>
                    : <ChartContainer height={height} xAxis={[{ id: "t", scaleType: "band", data: xLabels, tickLabelStyle: { fontSize: 10 } }]} yAxis={yAxes} series={series}>
                        {children}
                        <ChartsXAxis axisId="t" />
                        {yAxes.map(y => <ChartsYAxis key={y.id} axisId={y.id} />)}
                        <ChartsTooltip />
                        <ChartsLegend />
                    </ChartContainer>}
            </Paper>
        );
    }

    renderCharts() {
        const { requests, atlas, azure } = this.state.data;
        const rs = requests.series, as = atlas.series, zs = azure.series, av = azure.availability.series;
        return (
            <>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {this.chart("Requests (ok / failed) and p95 latency", [
                        { type: "bar", id: "ok", yAxisId: "n", data: rs.map(r => r.ok), label: "OK", stack: "req", color: "#2e7d32" },
                        { type: "bar", id: "err", yAxisId: "n", data: rs.map(r => r.err), label: "Failed", stack: "req", color: "#d32f2f" },
                        { type: "bar", id: "rej", yAxisId: "n", data: rs.map(r => r.rejected), label: "Rejected", stack: "req", color: "#ed6c02" },
                        { type: "line", id: "p95", yAxisId: "ms", data: rs.map(r => r.p95), label: "p95 ms", color: "#1976d2" },
                    ], rs.map(r => this.timeLabel(r.t)), [{ id: "n" }, { id: "ms", position: "right" }], <><BarPlot /><LinePlot /><MarkPlot /></>)}
                    {this.chart("Availability probes (all locations)", [
                        { type: "line", id: "pct", yAxisId: "pct", data: av.map(r => r.pct), label: "Uptime %", color: "#2e7d32" },
                        { type: "line", id: "pms", yAxisId: "ms", data: av.map(r => r.ms), label: "Probe ms", color: "#1976d2" },
                    ], av.map(r => this.timeLabel(r.t)), [{ id: "pct", min: 0, max: 100 }, { id: "ms", position: "right" }], <><LinePlot /><MarkPlot /></>)}
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {this.chart("MongoDB Atlas — connections and ops/s", [
                        { type: "line", id: "conn", yAxisId: "conn", data: as.map(r => r.connMax), label: "Connections (max)", color: "#6a1b9a" },
                        { type: "line", id: "ops", yAxisId: "ops", data: as.map(r => r.ops), label: "Ops/s", color: "#1976d2" },
                    ], as.map(r => this.timeLabel(r.t)), [{ id: "conn" }, { id: "ops", position: "right" }], <><LinePlot /><MarkPlot /></>)}
                    {this.chart("Azure Functions — instances serving and CPU", [
                        { type: "line", id: "inst", yAxisId: "inst", data: zs.map(r => r.instances), label: "Instances serving", color: "#6a1b9a" },
                        { type: "line", id: "ar", yAxisId: "inst", data: zs.map(r => r.alwaysReady), label: "Always-ready", color: "#00838f" },
                        { type: "line", id: "cpu", yAxisId: "cpu", data: zs.map(r => r.cpu), label: "CPU avg %", color: "#d32f2f" },
                        { type: "line", id: "cpumax", yAxisId: "cpu", data: zs.map(r => r.cpuMax), label: "CPU max %", color: "#ef9a9a" },
                    ], zs.map(r => this.timeLabel(r.t)), [{ id: "inst" }, { id: "cpu", position: "right", min: 0, max: 100 }], <><LinePlot /><MarkPlot /></>)}
                </Box>
            </>
        );
    }

    renderTables() {
        const { geo, azure, requests } = this.state.data;
        const totalReq = requests.ok + requests.err + requests.rejected;
        const locColumns = [
            { id: "location", label: "Probe location", render: l => locationName(l.location) },
            { id: "pct", label: "Uptime", align: "right", render: l => <Chip size="small" color={l.pct >= 99.9 ? "success" : l.pct >= 99 ? "warning" : "error"} label={pct(l.pct)} /> },
            { id: "ms", label: "Latency (avg)", align: "right", render: l => ms(l.ms) },
            { id: "samples", label: "Samples", align: "right", render: l => num(l.samples) },
        ];
        const geoColumns = [
            { id: "country", label: "Country", render: g => g.country },
            { id: "requests", label: "Requests", align: "right", render: g => num(g.requests) },
            { id: "share", label: "Share", align: "right", render: g => totalReq > 0 ? pct(g.requests / totalReq * 100, 1) : "—" },
        ];
        return (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Box sx={{ flex: 1, minWidth: 420 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Latency across geos (Azure availability tests → /api/Health)</Typography>
                    {azure.availability.locations.length
                        ? <DataTable columns={locColumns} rows={azure.availability.locations} getRowKey={l => l.location} />
                        : <Typography variant="body2" color="text.secondary">No probe results yet.</Typography>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 420 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Player requests by country</Typography>
                    {geo.length
                        ? <DataTable columns={geoColumns} rows={geo} getRowKey={g => g.code} />
                        : <Typography variant="body2" color="text.secondary">No player requests in this range.</Typography>}
                </Box>
            </Box>
        );
    }

    renderSettings() {
        const { settingsOpen, settings, saving } = this.state;
        if (!settings) return null;
        const field = (key, label, type = "number") => (
            <TextField key={key} label={label} type={type} size="small" value={settings[key] ?? ""} fullWidth sx={{ mb: 2 }}
                       onChange={e => this.setState({ settings: { ...settings, [key]: e.target.value } })} />
        );
        return (
            <FormDialog open={settingsOpen} title="Limits" onClose={() => this.setState({ settingsOpen: false })} onConfirm={this.saveSettings}
                        confirmText="Save" confirmDisabled={saving} maxWidth="sm" fullWidth>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Usage is compared against these limits. Update them when the Atlas tier or the Function App scale settings change.
                </Typography>
                {field("atlasTier", "Atlas tier", "text")}
                {field("atlasConnections", "Atlas connection limit")}
                {field("atlasStorageGB", "Atlas storage (GB)")}
                {field("atlasRamGB", "Atlas RAM (GB)")}
                {field("maxInstances", "Function App maximum instance count")}
                {field("instanceMemoryMB", "Instance memory (MB)")}
                {field("alwaysReady", "Always-ready HTTP instances")}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Alerts (global notifications: Admins → Set Notifications → Global)</Typography>
                <FormControlLabel sx={{ mb: 1 }} control={<Checkbox checked={!!settings.alertsEnabled} onChange={e => this.setState({ settings: { ...settings, alertsEnabled: e.target.checked } })} />}
                                  label="Send probe-down / usage-high / replication-lag notifications" />
                {field("warnPct", "Usage warning threshold (% of limit)")}
                {field("clearPct", "Usage clear threshold (% of limit)")}
                {field("reminderHours", "Reminder while a condition persists (hours)")}
                {field("replLagWarnSec", "Replication lag warning (seconds)")}
                {field("confirmSamples", "Confirmation window (consecutive samples that must agree; Atlas 1 min, Azure 5 min each)")}
            </FormDialog>
        );
    }

    render() {
        const { data, hours, loadedAt } = this.state;
        return (
            <PageContainer maxWidth={1400}>
                <SectionHeader title="System Health">
                    {loadedAt && <Typography variant="caption" color="text.secondary">updated {relTime(loadedAt)}</Typography>}
                    <ToggleButtonGroup size="small" exclusive value={hours} onChange={this.setRange}>
                        {RANGES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Limits"><span><IconButton size="small" onClick={this.openSettings} disabled={!data}><SettingsIcon fontSize="small" /></IconButton></span></Tooltip>
                </SectionHeader>
                {!data
                    ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    : <>
                        {this.renderCards()}
                        {this.renderCharts()}
                        {this.renderTables()}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                            Request telemetry is aggregated in memory per instance and stored once per 5 minutes — those rows are also the instance census
                            and carry each instance's CPU (Azure Monitor's instance and CPU metrics do not reflect Flex Consumption scale-out); Atlas is sampled
                            every minute (serverStatus/dbStats), Azure Monitor every 5 minutes for memory, executions and the probes; all samples expire after 7 days.
                        </Typography>
                    </>}
                {this.renderSettings()}
            </PageContainer>
        );
    }
}
